import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './Dashboard.css';
import appConfig from '../../utils/appConfig';
import { Typography, TextField, Button, CircularProgress, Box, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import terms_approval from '../../utils/termsAproval';
import stations from '../../utils/stations';
import Paper from '@mui/material/Paper';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { getOrCreateSchedulingId } from "../../utils/zohoService";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [idNumber, setIdNumber] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [studentNotAssociated, setStudentNotAssociated] = useState<boolean>(false);
  const [idError, setIdError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [selectedStation, setSelectedStation] = useState('');
  const [, setMosadName] = useState<string | null>(null);
  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [externalSchoolsForm, setExternalSchoolsForm] = useState<boolean>(false);
  const [serverData, setServerData] = useState({
    name: '',
    institution: '',
    price: '',
    eligible: '',
  });

  const [grade, setGrade] = useState('');
  const [parentName, setParentName] = useState('');
  const [phone, setPhone] = useState('');
  // כתובת התלמיד - נאסף רק בטופס ההסעות לבתי ספר מחוץ לעיר, ונשמר לאיש הקשר בזוהו.
  const [street, setStreet] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [city, setCity] = useState('');
  const [amount, setAmount] = useState('');
  const [currentYear, setCurrentYear] = useState<string | null>(null);
  // רשימת התחנות מגיעה מהגדרות המערכת (שהאדמין עורך). נופלים חזרה לרשימה
  // הסטטית רק אם הקריאה לשרת נכשלת.
  const [stationOptions, setStationOptions] = useState<string[]>(
    stations.stationsList.map(station => station.name)
  );

  useEffect(() => {
    fetch(`${appConfig.apiUrls.baseUrl}/settings/public`)
      .then(r => r.json())
      .then(d => {
        if (d.currentYear) setCurrentYear(d.currentYear);
        if (Array.isArray(d.stations) && d.stations.length > 0) setStationOptions(d.stations);
      })
      .catch(() => {});
  }, []);

  const { authorityName } = useParams<{ authorityName: string }>();
  const authority = appConfig.authorities.find(auth => auth.en === authorityName);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'idNumber') {
      setIdNumber(value);
      validateIdNumber(value);
    } else if (name === 'email') {
      setEmail(value);
      validateEmail(value);
    }
    else if (name.includes("Phone") || name.includes("phone")) {
      setPhone(value);
      validatePhone(value);
    }
  };

  const isValidIsraeliID = (id: string): boolean => {
    if (id.length !== 9) return false;
    return Array.from(id, Number).reduce((counter, digit, i) => {
      const step = digit * ((i % 2) + 1);
      return counter + (step > 9 ? step - 9 : step);
    }) % 10 === 0;
  };
  
  const validateIdNumber = (value: string) => {
    if (!/^\d*$/.test(value)) {
      setIdError('ניתן להקיש ספרות בלבד');
    } else if (value.length === 9) {
      if (isValidIsraeliID(value)) {
        setIdError(null); // מנקה שגיאות כאשר מספר תקין
        } else {
          setIdError('מספר תעודת זהות לא תקין');
        }
    } else {
      setIdError(value.length > 0 ? 'מספר תעודת זהות לא תקין' : '');
    }
  };
  

  const validateEmail = (value: string) => {
    if (!/\S+@\S+\.\S+/.test(value)) {
      setEmailError('כתובת אימייל לא תקינה');
    } else {
      setEmailError(null);
    }
  };

  const validatePhone = (value: string) => {
    if (!/^\d*$/.test(value)) {
      setPhoneError('ניתן להקיש ספרות בלבד');
    } else if (!value.startsWith('05') || value.length !== 10) {
      setPhoneError('מספר טלפון לא תקין, יש להקליד עשר ספרות בתבנית: 0501234567');
    }
    else {
      setPhoneError(null);
    }
  };

const handleCheckId = async () => {
    setLoading(true);
    setError(null);
    setStudentNotAssociated(false);
    setResult(null);

    try {
        const response = await fetch(`${appConfig.apiUrls.baseUrl}/sheets/verify/${idNumber}`);

        const text = await response.text();

        let data;
        try {
            data = JSON.parse(text);
        } catch {
            setError('תגובה לא תקינה מהשרת');
            return;
        }

        if (!response.ok || !Array.isArray(data) || data.length === 0) {
            setStudentNotAssociated(true);
            return;
        }

        const mosadName = data[0]?.mosad_name;
        setMosadName(mosadName);

        // בדיקת שיבוץ קיים לשנה הנוכחית ב-Zoho, כדי שלא ליצור שיבוץ כפול
        const yearToCheck = currentYear || appConfig.year;
        try {
            const schedRes = await fetch(`${appConfig.apiUrls.baseUrl}/zohoStudents/schedulingById/${idNumber}/${yearToCheck}`);
            const schedData = await schedRes.json();
            const existing = Array.isArray(schedData?.data) && schedData.data.length > 0 ? schedData.data[0] : null;

            if (existing) {
                // יש חוב רק אם debt > 0, למעט: הוראת קבע פעילה, או תלמיד זכאי (רשום) -
                // שני המקרים האלה נחשבים כאילו אין חוב. Payreceived אינו שדה אמין -
                // הוורקפלואו של "חשב סכומים" לא מעדכן אותו.
                const noDebt = Number(existing.debt) <= 0 || existing.DirectDebit === 'הוראת קבע פעילה' || existing.field37 === 'זכאי';

                if (noDebt) {
                    setError('התלמיד כבר משובץ לשנה זאת');
                    return;
                }

                // יש חוב על השיבוץ הקיים - ממשיכים עם הפרטים שלו לבחירת סוג תשלום
                setSelectedStation(existing.field36 || '');
                setServerData({
                    name: `${data[0].first_name} ${data[0].last_name}`,
                    institution: existing.field35 || mosadName,
                    price: existing.Annual_charge || data[0].amount,
                    eligible: existing.field37 === 'זכאי' ? 'זכאי' : 'לא זכאי',
                });
                setAmount(existing.Annual_charge || data[0].amount);
                return;
            }
        } catch {
            // לא קריטי - ממשיכים ברישום חדש אם בדיקת השיבוץ נכשלה
        }

        setServerData({
            name: `${data[0].first_name} ${data[0].last_name}`,
            institution: mosadName,
            price: data[0].amount,
            eligible: data[0].zakai === 'זכאי' ? 'זכאי' : 'לא זכאי'
        });

        const isNotEligible = data[0].zakai === '' || data[0].zakai === 'לא זכאי';
        const isFree = data[0].amount === '0';

        setAmount(data[0].amount);
        if (isFree && isNotEligible) {
            navigate(`/${authority?.en}/noPayments`, { state: { name: data[0].first_name + ' ' + data[0].last_name, institution: mosadName } });
        }
    } catch (err) {
        let errorMessage = 'אירעה שגיאה במהלך הבדיקה';

        if (err instanceof Error) {
            errorMessage = err.message;
        } else if (err && typeof err === 'object' && 'message' in err) {
            errorMessage = (err as any).message;
        }

        setError(errorMessage);
    } finally {
        setLoading(false);
    }
};


  const handlePayment = async (method: string) => {
  if ((!selectedStation && !externalSchoolsForm) || !acceptTerms) {
    setError('יש למלא את כל השדות ולהסכים לתנאים');
    return;
  }

  setLoading(true);

  try {
    const schedulingId = await getOrCreateSchedulingId(
      serverData, idNumber, email, selectedStation, method,
      { ...(currentYear ? { yearOverride: currentYear } : {}), phone, street, houseNumber, city }
    );
    if (!schedulingId) return;

    console.log("מזהה השיבוץ:", schedulingId);
    console.log('Selected Station:', selectedStation);

    const routeBase = `/${authority?.en}`;
    const finalAmount = externalSchoolsForm ? appConfig.price : amount; // ⬅️ משתמשים בערך מקומי

    switch (method) {
      case "pay_hok_cc":
        navigate(`${routeBase}/creditcardHok`, {
          state: { idNumber, email, selectedStation, serverData, acceptTerms, schedulingId, period: currentYear || appConfig.year },
        });
        break;

      case "pay_cc":
        navigate(`${routeBase}/creditcardPay`, {
          state: { idNumber, email, selectedStation, serverData, acceptTerms, grade, parentName, phone, amount: finalAmount, externalSchoolsForm, schedulingId, period: currentYear || appConfig.year },
        });
        break;

      case "cash_or_check":
        navigate(`${routeBase}/cashOrCheckPay`, {
          state: { idNumber, email, selectedStation, serverData, acceptTerms, grade, parentName, phone, amount: finalAmount, externalSchoolsForm, schedulingId },
        });
        break;

      case "noPayment":
        navigate(`${routeBase}/NoPaymentThankYouPage`, {
          state: { idNumber, email, selectedStation, serverData, acceptTerms, schedulingId },
        });
        break;

      default:
        console.warn("שיטת תשלום לא מזוהה:", method);
        break;
    }
  } catch (error) {
    console.error("שגיאה בתהליך התשלום:", error);
    setError("אירעה שגיאה. נסה שוב.");
  } finally {
    setLoading(false);
  }
};
      
  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleLabelClick = (event: any) => {
    event.preventDefault();
    handleDialogOpen();
  };

  const phoneNumber = '972585511320'; // מספר בפורמט בינלאומי ללא ה-0 הראשון
  const message = 'שלום, אני מעוניין לרשום ילד שאינו במערכת.';
  
  const handleWhatsAppClick = () => {
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const notAssociatedPhoneNumber = '972505818258'; // 050-581-8258 בפורמט בינלאומי ללא ה-0 הראשון

  const handleNotAssociatedWhatsAppClick = () => {
    const notAssociatedMessage = `שלום, אני פונה בנוגע לרישום להסעות עבור ת.ז ${idNumber}`;
    const whatsappUrl = `https://wa.me/${notAssociatedPhoneNumber}?text=${encodeURIComponent(notAssociatedMessage)}`;
    window.open(whatsappUrl, '_blank');
  };

  // סטייט עבור ניהול האינפוט הפתוח ושם המוסד
const [editInstitution, setEditInstitution] = useState(false);
const [newInstitution, setNewInstitution] = useState(serverData.institution);

const handleInstitutionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  setNewInstitution(event.target.value);
};

const handleInstitutionUpdate = () => {
  // עדכון ה-serverData עם השם החדש של המוסד
  serverData.institution = newInstitution;
  setEditInstitution(false);
};

const handleExternalSchoolsForm = () => {
  // alert('שלום וברכה!\nהרישום להסעות נסגר - לבירורים נוספים יש לפנות למחלקת ההסעות בטלפון 089279995');
  //               return; 
  setExternalSchoolsForm(true);
  setServerData(prev => ({...prev, price: appConfig.price}));
};

const grades = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט', 'י', 'י"א', 'י"ב'];
  

  return (
    <div className="dashboard-container">
      {!authorityName && (
        <div className="error-message">
          <h1>יש לרשום את שם הרשות באנגלית בסוף ה-URL</h1>
        </div>
      )}
      {authorityName && serverData.name === '' &&!externalSchoolsForm&& (
        <>
          <Typography align="center" variant="h1">
            ברוכים הבאים למערכת רישום <br></br> להסעות בתי ספר עיריית {authority?.he}
          </Typography>
          <Typography align="center" variant="h5">
            אנא הקש פרטי זיהוי של התלמיד
          </Typography>
          <TextField
            fullWidth
            label="מספר תעודת זהות של התלמיד"
            variant="outlined"
            margin="normal"
            name="idNumber"
            value={idNumber}
            onChange={handleChange}
            inputProps={{ style: { textAlign: 'center' } }}
            error={!!idError} // מציג שגיאה אם יש
            helperText={idError} // מראה את הודעת השגיאה
          />
          <TextField
            fullWidth
            label="אימייל לקבלת חשבונית"
            variant="outlined"
            margin="normal"
            type="email"
            name="email"
            value={email}
            onChange={handleChange}
            inputProps={{ style: { textAlign: 'center' } }}
            error={!!emailError} // מציג שגיאה אם יש
            helperText={emailError} // מראה את הודעת השגיאה
          />
          {error && (
            <Typography color="error">
              {error}
            </Typography>
          )}
          {studentNotAssociated && (
            <Box sx={{ textAlign: 'right', direction: 'rtl', mt: 1, mb: 1 }}>
              <Typography color="error">
                הילד אינו משוייך לתוכנית ההסעות ואינו יכול להירשם להסעה.
              </Typography>
              <Typography color="error">
                ניתן לפנות למחלקת הסעות במייל: edu_matans@lod.muni.il
              </Typography>
              <Typography color="error" sx={{ mb: 1.5 }}>
                או בוואטסאפ (בצירוף לבקשה יש לשלוח מספר ת.ז של התלמיד)
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={handleNotAssociatedWhatsAppClick}
                startIcon={<WhatsAppIcon />}
                style={{ backgroundColor: '#25D366', color: '#fff' }}
              >
                לפניה למח' הסעות
              </Button>
            </Box>
          )}
          <Box display="flex" flexDirection="column" gap="16px">
          <Button
            variant="contained"
            color="primary"
            onClick={handleCheckId}
            disabled={loading || idError !== null|| emailError !== null||!idNumber||!email}
            style={{ marginTop: '16px' }}
          >
            {loading ? <CircularProgress size={24} /> : 'כניסה'}
          </Button>
          {/* כפתור לרישום ילד שלומד מחוץ לעיר */}
            <Button
              variant="contained"
              onClick={handleExternalSchoolsForm}
              style={{ marginTop: '16px', backgroundColor: '#4fc3f7', color: '#fff' }} // תכלת
            >
              הסעות לבתי ספר מחוץ לעיר
            </Button>

            <Button
              variant="contained" // שונה מ-outlined
              color="secondary"
              onClick={handleWhatsAppClick}
              style={{ marginTop: '16px' }}
            >
              פניה לשרות לקוחות
              <WhatsAppIcon style={{ marginLeft: '8px', fontSize: '15px' }} />
            </Button>
            </Box>
        </>
      )}
      {externalSchoolsForm && (
       <Box
       display="flex"
       flexDirection="column"
       justifyContent="center"
       alignItems="center"
       height="100vh"
       textAlign="center"
     >
       <Typography variant="h4" gutterBottom>
         טופס הסעות לבתי ספר מחוץ לעיר
       </Typography>
       <FormControl fullWidth margin="normal">
         <InputLabel id="school-select-label">בחר בית ספר</InputLabel>
         <Select
           labelId="school-select-label"
           value={serverData.institution}
           onChange={(e) => setServerData({ ...serverData, institution: e.target.value })}

           label="בחר בית ספר"
         >
           <MenuItem value="אלעד בנים">אלעד בנים</MenuItem>
           <MenuItem value="אלעד בנות">אלעד בנות</MenuItem>
           <MenuItem value="רחובות מעיין חי">רחובות מעיין חי</MenuItem>
           <MenuItem value="מודיעין עילית">מודיעין עלית בנות</MenuItem>
         </Select>
       </FormControl>
        <TextField
            fullWidth
            label="מספר תעודת זהות של התלמיד"
            variant="outlined"
            margin="normal"
            name="idNumber"
            value={idNumber}
            onChange={handleChange}
            inputProps={{ style: { textAlign: 'center' } }}
            error={!!idError} // מציג שגיאה אם יש
            helperText={idError} // מראה את הודעת השגיאה
          />
          
        <TextField
         fullWidth
         label="שם התלמיד"
         variant="outlined"
         margin="normal"
         value={serverData.name}
         onChange={(e) => setServerData({ ...serverData, name: e.target.value })}
       />
       <FormControl fullWidth margin="normal">
        <InputLabel id="grade-select-label">בחר כיתה</InputLabel>
        <Select
          labelId="grade-select-label"
          value={grade}
          onChange={(e) => setGrade(e.target.value as string)}
          label="בחר כיתה"
        >
          {grades.map((grade, index) => (
            <MenuItem key={index} value={grade}>{`כיתה ${grade}`}</MenuItem>
          ))}
        </Select>
      </FormControl>
        <TextField
         fullWidth
         label="שם ההורה"
         variant="outlined"
         margin="normal"
         value={parentName}
         onChange={(e) => setParentName(e.target.value)}
       />
        <TextField
            fullWidth
            label="אימייל לקבלת חשבונית"
            variant="outlined"
            margin="normal"
            type="email"
            name="email"
            value={email}
            onChange={handleChange}
            inputProps={{ style: { textAlign: 'center' } }}
            error={!!emailError} // מציג שגיאה אם יש
            helperText={emailError} // מראה את הודעת השגיאה
          />
       <TextField
         fullWidth
         label="טלפון נייד"
         variant="outlined"
         margin="normal"
         name="phone"
         value={phone}
         onChange={handleChange}
         inputProps={{ style: { textAlign: 'center' } }}
         error={!!phoneError} // מציג שגיאה אם יש
         helperText={phoneError} // מראה את הודעת השגיאה
       />
       <TextField
         fullWidth
         label="רחוב"
         variant="outlined"
         margin="normal"
         value={street}
         onChange={(e) => setStreet(e.target.value)}
         inputProps={{ style: { textAlign: 'center' } }}
       />
       <TextField
         fullWidth
         label="מספר בית"
         variant="outlined"
         margin="normal"
         value={houseNumber}
         onChange={(e) => setHouseNumber(e.target.value)}
         inputProps={{ style: { textAlign: 'center' } }}
       />
       <TextField
         fullWidth
         label="עיר"
         variant="outlined"
         margin="normal"
         value={city}
         onChange={(e) => setCity(e.target.value)}
         inputProps={{ style: { textAlign: 'center' } }}
       />
       <Box sx={{ width: '100%', direction: 'rtl' }}>
          <FormControlLabel
            labelPlacement="start"
            control={
              <Checkbox
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
              />
            }
            label={
              <span
                onClick={handleLabelClick}
                style={{
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  display: 'inline-block',
                  textAlign: 'right',
                  width: '100%',
                }}
              >
                אישור תנאי רישום
              </span>
            }
            sx={{
              justifyContent: 'flex-start',
              width: '100%',
              m: 0,
            }}
          />
        </Box>

        <Dialog open={dialogOpen} onClose={handleDialogClose}>

          <DialogTitle sx={{ textAlign: 'center' }}>תנאי רישום</DialogTitle>
          <DialogContent sx={{ textAlign: 'left' }}>
            {/* התוכן של תנאי הרישום כאן */}
            <Typography
              variant="body1"
              style={{ textAlign: 'right', direction: 'rtl' }}
              dangerouslySetInnerHTML={{ __html: terms_approval.lod_buses_terms_approval }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} sx={{ margin: '0 auto' }}>סגירה</Button>
          </DialogActions>
        </Dialog>
       <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handlePayment("pay_cc")}
          disabled={
            loading || emailError !== null || idError !== null || phoneError !== null ||
            !idNumber || !email || !serverData.institution || !grade ||
            !parentName || !phone || !street || !houseNumber || !city || !acceptTerms
          }
        >
         {loading ? <CircularProgress size={24} color="inherit" /> : "תשלום באשראי"}
        </Button>
        
      </Box>
     </Box>
      )}
       {serverData.name!=='' &&!externalSchoolsForm&& (
  <Box
  sx={{
    position: 'relative',
    width: 400,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    m: 'auto',
    textAlign: 'center'
  }}
>
  <Paper
    sx={{
      padding: 3,
      borderRadius: 2,
      boxShadow: 4,
      border: '1px solid #ccc', // הוספת מסגרת נוספת
      textAlign: 'left',
      marginTop: 2,
      marginBottom: 2
    }}
    >
      <Typography variant="body1">
        <span style={{ fontWeight: 'bold' }}>שם:</span> {serverData.name}
      </Typography>
      <Typography variant="body1">
        <span style={{ fontWeight: 'bold' }}>מספר ת"ז:</span> {idNumber}
      </Typography>
      <Typography variant="body1">
        <span style={{ fontWeight: 'bold' }}>מוסד:</span> {serverData.institution}
      </Typography>
      <Typography variant="body1">
        <span style={{ fontWeight: 'bold' }}>מחיר:</span> {serverData.price}
      </Typography>
      <Typography variant="body1">
        <span style={{ fontWeight: 'bold' }}>זכאות ע"פ משרד החינוך:</span> {serverData.eligible}
      </Typography>
    </Paper>
     {/* כפתור לשינוי שם המוסד */}
     {/* <Button
  variant="outlined"
  color="secondary"
  onClick={() => setEditInstitution(!editInstitution)}
  style={{ marginTop: '16px' }}
>
  {editInstitution ? 'סגירה' : 'שינוי שם מוסד'}
</Button> */}

  {/* משבצת אינפוט לשם מוסד חדש */}
  {editInstitution && (
    <Box mt={2}>
      <TextField
        fullWidth
        label="שם מוסד חדש"
        variant="outlined"
        value={newInstitution}
        onChange={handleInstitutionChange}
        InputProps={{
          sx: { textAlign: 'center' ,direction: 'ltr'}, // יישור הטקסט לימין
        }}
       
      />
      <Button
        variant="contained"
        color="primary"
        onClick={handleInstitutionUpdate}
        style={{ marginTop: '8px' }}
      >
        עדכן מוסד
      </Button>
    </Box>
  )}
        <Box mt={2} sx={{ textAlign: 'left' }}>  {/* הוספת מרווח עליון */}
  <FormControl fullWidth>
    <InputLabel id="station-select-label">בחר תחנה</InputLabel>
    <Select
      labelId="station-select-label"
      value={selectedStation}
      onChange={(e) => setSelectedStation(e.target.value as string)}
      label="בחר תחנה"
      required
      MenuProps={{
        PaperProps: {
          sx: {
            textAlign: 'left',
            direction: 'ltr',
            marginTop: '10px',
          },
        },
      }}
    >
      {stationOptions.map((station, index) => (
        <MenuItem key={index} value={station}>
          {station}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
</Box>
<Box sx={{ width: '100%', direction: 'rtl' }}>
          <FormControlLabel
            labelPlacement="start"
            control={
              <Checkbox
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
              />
            }
            label={
              <span
                onClick={handleLabelClick}
                style={{
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  display: 'inline-block',
                  textAlign: 'right',
                  width: '100%',
                }}
              >
                אישור תנאי רישום
              </span>
            }
            sx={{
              justifyContent: 'flex-start',
              width: '100%',
              m: 0,
            }}
          />
        </Box>
  <Dialog open={dialogOpen} onClose={handleDialogClose}>
  <DialogTitle sx={{ textAlign: 'center' }}>תנאי רישום</DialogTitle>
  <DialogContent sx={{ textAlign: 'left' }}>
    {/* התוכן של תנאי הרישום כאן */}
    <Typography
      variant="body1"
      style={{ textAlign: 'right', direction: 'rtl' }}
      dangerouslySetInnerHTML={{ __html: terms_approval.lod_buses_terms_approval }}
    />
  </DialogContent>
  <DialogActions>
    <Button onClick={handleDialogClose} sx={{ margin: '0 auto' }}>סגירה</Button>
  </DialogActions>
</Dialog>

  <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '16px' }}>
    {/* {(serverData.eligible !== "זכאי")&&((!selectedStation.includes('גני אביב')||(mosadName==='מעוז חיים'))) ? ( */}
    {serverData.eligible !== "זכאי" ? (
      <>
         <Button
          variant="contained"
          color="primary"
          onClick={() => handlePayment("pay_hok_cc")}
          disabled={!selectedStation || !acceptTerms|| loading}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "תשלום בהוק באשראי"}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handlePayment("pay_cc")}
          disabled={!selectedStation || !acceptTerms|| loading}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "תשלום באשראי"}
        </Button>
      </>
    ) : (
      <Button
        variant="contained"
        color="primary"
        onClick={() => handlePayment("noPayment")}
        disabled={!selectedStation || !acceptTerms}
        style={{ marginTop: '16px' }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : "הרשם"}
      </Button>
    )}
  </div>

  {error && <Typography variant="body2" color="error.main" style={{ marginTop: '16px' }}>{error}</Typography>}
</Box>
      )}
    </div>
  );
};

export default Dashboard;
